import { useSpring, animated } from "react-spring";
import { useState } from "react";
import { ChevronDown, Star, User } from "lucide-react";

const AccordionReviews = ({ author, avatar_path, rating, content, created_at }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { height, opacity } = useSpring({
    from: { height: 0, opacity: 0 },
    to: { height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 },
  });

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "";
    }
  };

  // Get avatar URL - handle different avatar_path formats
  const getAvatarUrl = () => {
    if (!avatar_path) return null;
    if (avatar_path.startsWith("http")) return avatar_path;
    if (avatar_path.startsWith("/")) {
      return `https://image.tmdb.org/t/p/w500${avatar_path}`;
    }
    return `https://image.tmdb.org/t/p/w500${avatar_path}`;
  };

  return (
    <div className="border-b border-white/10 last:border-b-0 overflow-hidden">
      <div
        className="flex items-center justify-between cursor-pointer p-5 md:p-6 hover:bg-gray/20 transition-all duration-300 group"
        onClick={toggleAccordion}
      >
        <div className="flex items-center gap-4 flex-1 min-w-0">
          {/* Avatar */}
          <div className="flex-shrink-0">
            {getAvatarUrl() ? (
              <img
                src={getAvatarUrl()}
                alt={author || "Reviewer"}
                className="w-12 h-12 md:w-14 md:h-14 rounded-full border-2 border-white/10 group-hover:border-red/50 transition-colors duration-300 object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div className={`w-12 h-12 md:w-14 md:h-14 rounded-full bg-gray/30 border-2 border-white/10 flex items-center justify-center ${getAvatarUrl() ? 'hidden' : 'flex'}`}>
              <User className="text-white/40" size={24} />
            </div>
          </div>

          {/* Author Info */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-1">
              <h3 className="text-white poppins font-semibold text-base md:text-lg">
                {author || "Anonymous"}
              </h3>
              {rating !== null && rating !== undefined && (
                <div className="flex items-center gap-1 bg-black/60 backdrop-blur-sm px-2 py-1 rounded-full border border-white/10">
                  <Star className="text-yellow-400" size={12} fill="#FBBF24" />
                  <span className="text-white mont text-xs font-semibold">{rating}/10</span>
                </div>
              )}
            </div>
            {created_at && (
              <p className="text-white/50 mont text-xs md:text-sm">
                {formatDate(created_at)}
              </p>
            )}
          </div>
        </div>

        {/* Toggle Icon */}
        <div className="flex-shrink-0 pl-4">
          <animated.div
            style={{
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease',
            }}
          >
            <ChevronDown className="text-white/60 group-hover:text-red transition-colors duration-300" size={24} />
          </animated.div>
        </div>
      </div>

      {/* Review Content */}
      <animated.div
        style={{ height, opacity }}
        className="overflow-hidden transition-all duration-300"
      >
        <div className="px-5 md:px-6 pb-5 md:pb-6 pt-0">
          <div className="pl-16 md:pl-20">
            <p className="text-white/80 mont text-sm md:text-base leading-relaxed whitespace-pre-wrap">
              {content}
            </p>
          </div>
        </div>
      </animated.div>
    </div>
  );
};

export default AccordionReviews;
